import _defineProperty from '@babel/runtime/helpers/defineProperty';

/* eslint-disable compat/compat */
function getDefaultRoot() {
  /*
     Use document if supported
     IntersectionObserver will otherwise default to top level document,
     and IntersectionObserver will not work as expected in an iframe.
      https://github.com/w3c/IntersectionObserver/issues/372
    */

  /*
  Safari does not throw error on root: document, but does not trigger events as expected
  returning null for now until better feature detection is possible
  */
  return null;
  /*
  try {
    // eslint-disable-next-line no-new
    new IntersectionObserver(() => {}, { root: document })
    return document
  } catch (e) {
    return null
  }
  */
}

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var defaultConfig = {
  rootMargin: '0px 0px 0px 0px',
  threshold: [0, 0.5, 1],
  root: getDefaultRoot()
};
class ElementIntersectionObserver {
  constructor(elements, onChange, config) {
    _defineProperty(this, "intersections", void 0);

    _defineProperty(this, "elements", void 0);

    this.elements = elements.slice(0);
    this.intersections = elements.map(target => ({
      target,
      isIntersecting: false,
      ratio: null
    }));
    this.intersectionObserver = new IntersectionObserver(entries => {
      var intersections = this.intersections.slice(0);
      entries.forEach(entry => {
        var idx = elements.indexOf(entry.target);
        intersections.splice(idx, 1, {
          target: entry.target,
          isIntersecting: entry.isIntersecting,
          ratio: entry.intersectionRatio
        });
      });
      this.intersections = intersections;
      onChange(this.intersections);
    }, _objectSpread(_objectSpread({}, defaultConfig), config));
    elements.forEach(elm => this.intersectionObserver.observe(elm));
  }

  disconnect() {
    // Unregister trigger elements
    this.elements.forEach(elm => this.intersectionObserver.unobserve(elm)); // Disconnect intersection observer

    this.intersectionObserver.disconnect();
  }

}

function toggleAttribute(el, name, boolValue) {
  if (!el) {
    return;
  }

  var nextValue;

  if (typeof boolValue !== 'undefined') {
    nextValue = !!boolValue;
  } else {
    nextValue = el.hasAttribute(name);
  }

  if (nextValue) {
    el.setAttribute(name, '');
  } else {
    el.removeAttribute(name);
  }
}
function setAttribute(el, name, nextValue) {
  if (!el) {
    return;
  }

  if (nextValue) {
    el.setAttribute(name, nextValue);
  } else {
    el.removeAttribute(name);
  }
}
var closest = (() => {
  var proto = typeof window === 'undefined' ? {} : window.Element.prototype;
  var match = proto.matches || proto.msMatchesSelector || proto.webkitMatchesSelector;

  if (proto.closest) {
    return (el, css) => el.closest(css);
  } else {
    return (el, css) => {
      for (; el; el = el.parentElement) {
        if (match.call(el, css)) return el;
      }

      return null;
    };
  }
})();
function addStyle(nodeName, css) {
  var key = "style-".concat(nodeName.toLowerCase());
  var min = css.replace(/\/\*[^!][^*]*\*\//g, '').replace(/\s*(^|[:;,{}]|$)\s*/g, '$1');
  document.getElementById(key) || document.head.insertAdjacentHTML('afterbegin', "<style id=\"".concat(key, "\">").concat(min, "</style>"));
}
function toArray(nodeList) {
  return [].slice.call(nodeList);
}
function dispatchEvent(element, name) {
  var detail = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var ignore = "prevent_recursive_dispatch_maximum_callstack".concat(name);
  var event;
  if (element[ignore]) return true; // We are already processing this event, so skip sending a new one
  else element[ignore] = true; // Add name to dispatching ignore

  if (typeof window.CustomEvent === 'function') {
    event = new window.CustomEvent(name, {
      bubbles: true,
      cancelable: true,
      detail
    });
  } else {
    event = document.createEvent('CustomEvent');
    event.initCustomEvent(name, true, true, detail);
  } // IE reports incorrect event.defaultPrevented
  // but correct return value on element.dispatchEvent


  var result = element.dispatchEvent(event);
  element[ignore] = null; // Remove name from dispatching ignore

  return result; // Follow W3C standard for return value
}

function easeOutQuint(t) {
  return 1 + --t * t * t * t * t;
}

class VerticalScroll {
  constructor(container) {
    _defineProperty(this, "container", void 0);

    this.container = container;
  }

  cancel() {
    this._cancelled = true;
  }

  to(top) {
    var duration = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1000;
    var callback = arguments.length > 2 ? arguments[2] : undefined;
    var from = this.container.scrollTop;
    var distance = top - from;
    var startTime = null;

    var loop = currentTime => {
      if (this._cancelled) {
        return;
      }

      if (!startTime) {
        startTime = currentTime - 1;
      }

      var timeElapsed = currentTime - startTime;

      if (timeElapsed < duration) {
        this.container.scrollTop = from + distance * easeOutQuint(timeElapsed / duration);
        window.requestAnimationFrame(loop);
      } else {
        this.container.scrollTop = top;
        callback && callback();
      }
    };

    window.requestAnimationFrame(loop);
  }

}
class HorizontalScroll {
  constructor(container) {
    _defineProperty(this, "container", void 0);

    this.container = container;
  }

  cancel() {
    this._cancelled = true;
  }

  to(left) {
    var duration = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1000;
    var callback = arguments.length > 2 ? arguments[2] : undefined;
    var from = this.container.scrollLeft;
    var distance = left - from;
    var startTime = null;

    var loop = currentTime => {
      if (this._cancelled) {
        return;
      }

      if (!startTime) {
        startTime = currentTime - 1;
      }

      var timeElapsed = currentTime - startTime;

      if (timeElapsed < duration) {
        this.container.scrollLeft = from + distance * easeOutQuint(timeElapsed / duration);
        window.requestAnimationFrame(loop);
      } else {
        this.container.scrollLeft = left;
        callback && callback();
      }
    };

    window.requestAnimationFrame(loop);
  }

}

function testReducedMotion() {
  if (typeof window !== 'undefined') {
    return !!window.matchMedia && window.matchMedia('(prefers-reduced-motion)').matches;
  }

  return false;
}

var MAX_SAFE_INTEGER = 9007199254740991;
function withinRange(current) {
  var start = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  var stop = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : MAX_SAFE_INTEGER;
  return current >= start && current <= stop;
}
function withinStops(currentTime, stops, targetStop) {
  var nrOfStops = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 1;
  var uniqueStops = stops.filter(unique);
  var targetTime = stops[targetStop];
  var prevTime = uniqueStops[uniqueStops.indexOf(targetTime) - nrOfStops];
  var nextTime = uniqueStops[uniqueStops.indexOf(targetTime) + nrOfStops];
  return withinRange(currentTime, prevTime, nextTime);
}

function unique(value, index, self) {
  return self.indexOf(value) === index;
}

/*
 * 'loading="lazy"' is defined in terms of IntersectionObserver and the initial root.
 * As a result images with 'loading="lazy"' inside a scroll container will not load
 * until they at least have one pixel visible in the viewport.
 *
 * This will hopefully fixed in a future version of the spec:
 * https://github.com/w3c/IntersectionObserver/issues/431
 *
 * scroll-container-lazy-load observes images and a scroll container.
 * Once images are close to intersecting it will change 'loading="lazy"' to 'loading="eager"'.
 * */

function loadImage(image) {
  if (image.loading === 'lazy') {
    image.loading = 'eager';
  }
}

function connectScrollContainerLazyLoad(container, images) {
  var containerIntersecting = false;
  var deferredImages = []; // eslint-disable-next-line compat/compat

  var containerObserver = new IntersectionObserver(entries => {
    containerIntersecting = entries === null || entries === void 0 ? void 0 : entries[0].isIntersecting;

    if (containerIntersecting) {
      deferredImages.forEach(image => loadImage(image));
      deferredImages = [];
    }
  }, {
    root: getDefaultRoot(),
    threshold: [0.0]
  });
  containerObserver.observe(container); // eslint-disable-next-line compat/compat

  var imageObserver = new IntersectionObserver(entries => {
    for (var entry of entries) {
      if (entry.isIntersecting) {
        var image = entry.target;
        imageObserver.unobserve(image);

        if (containerIntersecting) {
          loadImage(image);
        } else {
          /* Defer loading of if the container is not intersecting the viewport */
          deferredImages.push(image);
        }
      }
    }
  }, {
    root: container,
    rootMargin: '300px',
    threshold: 0.0
  });

  for (var image of images) {
    imageObserver.observe(image);
  }

  return () => {
    containerObserver.disconnect();
    imageObserver.disconnect();
  };
}

function debounce(fn, delay) {
  var timeout;
  return function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
}

export { ElementIntersectionObserver, HorizontalScroll, VerticalScroll, addStyle, closest, connectScrollContainerLazyLoad, debounce, dispatchEvent, setAttribute, testReducedMotion, toArray, toggleAttribute, withinRange, withinStops };
//# sourceMappingURL=dh-basis-common.esm.js.map
